import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { useSetAtom } from 'jotai';
import {
  Button,
  FeatureFlags,
  Search,
  Tag,
  TreeNode,
  TreeView,
} from '@carbon/react';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  SidePanelClose,
  SidePanelOpen,
  TrashCan,
} from '@carbon/icons-react';
import { OutputSection } from 'components/output-section/output-section';
import { withSubtractCoverage } from 'utils/subtract-coverage';
import {
  AssetPickerTearsheet,
  type PickedAsset,
} from './asset-picker-tearsheet';
import { CarryEditor } from './carry-editor';
import { ElectionEditor } from './election-editor';
import { useEval3Page } from './context';
import { evalSettingsPanelOpenAtom, interestModelDeltaAtom } from './atoms';
import {
  usePartialTracts,
  useTractWells,
  type WorkingConfigForm,
} from './hooks';
import {
  computeInterestModelDelta,
  EMPTY_INTEREST_MODEL_DELTA,
} from './interest-modeling-rollup';
import { buildCalculateRequest } from './utils';
import { CarryPortfolio } from './carry-portfolio';
import { ElectionResults } from './election-results';
import { useCarryPreview } from './use-carry-preview';
import { useElectionPreview } from './use-election-preview';
import type { ChartView } from './interest-modeling-preview';
import type { InterestItemKind } from './types';
import styles from './interest-modeling-section.module.scss';

/**
 * The interest kind for a picked instrument type: OGL → carry, UMI → election.
 *
 * @param {string} instrumentType - The picked tract's instrument type ('OGL' / 'UMI').
 * @return {InterestItemKind} The position kind.
 */
const kindForInstrument = (instrumentType: string): InterestItemKind => {
  return instrumentType === 'OGL' ? 'carry' : 'election';
};

/**
 * The instrument label shown on a tract's tag.
 *
 * @param {InterestItemKind} kind - The position kind.
 * @return {string} 'OGL' for carry, 'UMI' for election.
 */
const kindLabel = (kind: InterestItemKind): string => {
  return kind === 'carry' ? 'OGL' : 'UMI';
};

/**
 * The Carbon Tag color per instrument (OGL blue / UMI purple).
 *
 * @param {InterestItemKind} kind - The position kind.
 * @return {'blue' | 'purple'} The tag color.
 */
const kindTagType = (kind: InterestItemKind): 'blue' | 'purple' => {
  return kind === 'carry' ? 'blue' : 'purple';
};

export interface InterestModelingSectionProps {
  /** The lifted working-config form (owned by `Eval3NewContent`) — the interest-items store. */
  workingConfig: WorkingConfigForm;
}

/**
 * Interest Modeling section (INS-10673) — the Assets frame. A left tree
 * of interest positions (one node per modeled tract, its tract name + an OGL/UMI
 * Tag) beside a content area whose header steps through the positions and whose
 * body is a per-kind placeholder for the carry/election editors + results that
 * later stories build.
 *
 * State model: the position list lives in the working config as
 * `interestItems: [{ tractId, kind }]` — seeded from the loaded eval's committed
 * `configurations.interest_items`, dirty-tracked, and baked back into
 * `configurations` on Calculate (so it restores on reopen). Add/remove mutate that
 * working copy. Selection (`activeId`) and expansion (`openItemId`) are ephemeral
 * view state, independent of each other: clicking a node's LABEL selects it (drives
 * the content area); clicking its CARET expands the inline editor. The Search box
 * filters the rendered nodes only — the underlying list never loses an item.
 *
 * Case gate: interest modeling requires an evaluation case in effect. With no
 * case (a fresh package, or a loaded legacy/no-case calc), the section shows a
 * choose-a-case prompt that opens Eval Settings (where the case is picked); items
 * can't be managed until a case is chosen.
 *
 * @param {InterestModelingSectionProps} props - The lifted working-config form.
 * @return {JSX.Element} The Interest Modeling section.
 */
export function InterestModelingSection({
  workingConfig,
}: InterestModelingSectionProps) {
  const interestItems = useStore(
    workingConfig.form.store,
    (state) => state.values.interestItems
  );
  const evaluationCaseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  const openEvalSettings = useSetAtom(evalSettingsPanelOpenAtom);

  // Selection (which position the content area shows) and expansion (which node's
  // inline editor is open) are independent ephemeral view state.
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [tractQuery, setTractQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  // The shown position: the selected one, else the first (so a fresh selection or
  // a removal of the active item always lands on something while items remain).
  const activeItem =
    interestItems.find((item) => item.tractId === activeId) ??
    interestItems[0] ??
    null;
  const activeIndex = activeItem
    ? interestItems.findIndex((item) => item.tractId === activeItem.tractId)
    : -1;
  // Prev/next walks the FULL list, never the filtered view — hiding items behind a
  // search must not silently shorten this navigation.
  const stepActive = (delta: number) => {
    const next = interestItems[activeIndex + delta];
    if (next) {
      setActiveId(next.tractId);
    }
  };

  // the carry group well roster. Sourced from the existing
  // `assets` master_data view (calc mode; `useTractWells`), fetched only when
  // there's a carry item to serve; each carry editor reads its tract's wells
  // from this map (keyed by the same tract/row id the picker uses).
  const { packageId, calculationId, workspaceId, isDraft } = useEval3Page();
  // Both carry (well groups) and election (well scope + $/NMA acreage) read the roster,
  // so fetch it whenever there is any interest item to serve.
  const hasModelableItems = interestItems.length > 0;
  // The modeled tract ids the roster must cover — so a selection beyond the assets
  // view's first page still resolves its wells (useTractWells pages until all are in).
  const selectedTractIds = useMemo(
    () => interestItems.map((item) => String(item.tractId)),
    [interestItems]
  );
  const {
    wellsByTract,
    netAcresByTract,
    isLoading: wellsLoading,
  } = useTractWells(
    packageId,
    calculationId,
    hasModelableItems,
    selectedTractIds
  );

  // DRAFT (eval/new): the preview endpoints have no calc row to read a config from,
  // so each request carries the draft's working config, serialized by the SAME
  // `buildCalculateRequest` Calculate uses (the BE normalizes it with the same
  // helpers — no second serializer on either side). The builder reads form.state at
  // dispatch time so it always ships the current values; undefined off a draft, so
  // real evals keep the member-route path untouched.
  //
  // A case's draft: the case-level wire, which buildCalculateRequest takes from the form's case —
  // the form's `{ domain: id }` overrides, and no Type Curve or DCA — so a "this run only" override
  // reprices the carry and election previews as it does the run. Built from the form itself, never
  // from run_scenarios' answer, so the previews never wait on the check (or fall back to the legacy
  // wire while it is pending or failed), and an override change resets their session once.
  const buildDraftRequest = useMemo(() => {
    if (!isDraft || workspaceId === null) {
      return undefined;
    }
    return () =>
      buildCalculateRequest(workingConfig.form.state.values, {
        workspaceId,
        packageId,
      });
  }, [isDraft, workspaceId, packageId, workingConfig]);

  // DRAFT session identity: the pricing-relevant slice of the live working config.
  // A committed calc's config is frozen, but a draft's changes under the preview —
  // when it does, the prepared frame and every cached tract were priced under the
  // OLD config, so the hooks reset their session and re-warm. Built from the same
  // request serializer with the items zeroed, minus the non-pricing fields
  // (description/notes/fork flag), so an item edit or a note never resets the
  // session while a date/case/deck/override change always does. The selector
  // returns a string, so the subscription only re-renders when the slice changes.
  const draftSessionKey = useStore(workingConfig.form.store, (state) => {
    if (!isDraft || workspaceId === null) {
      return undefined;
    }
    const request = buildCalculateRequest(
      { ...state.values, interestItems: [] },
      { workspaceId, packageId }
    );
    return JSON.stringify({
      workspaceId: request.workspaceId,
      packageId: request.packageId,
      scenarioId: request.scenarioId,
      priceDeckId: request.priceDeckId,
      configurations: request.configurations,
    });
  });

  // The live carry-portfolio preview session. Warms every
  // complete carry tract automatically (it watches `interestItems`, so add is wired
  // implicitly), reprices the active tract on a term-edit blur, and drops a removed
  // tract; its recombined portfolio + per-tract cache feed the results surface. The
  // chart view lives here (not in the surface) so it survives navigating between
  // positions and so the hook can fetch the active tract's byProperty slots on demand.
  const [view, setView] = useState<ChartView>('aggregate');
  const preview = useCarryPreview({
    interestItems,
    view,
    activeTractId: activeItem?.tractId ?? null,
    wellsByTract,
    // Hold the warm drain until the roster fetch settles so every warmed tract is
    // priced with its unassigned wells (a failed/empty fetch flips this false, so the
    // preview is never blocked by it).
    rosterLoading: wellsLoading,
    buildDraftRequest,
    draftSessionKey,
  });
  // The live UMI election preview session — a focused per-tract sibling of
  // the carry preview (no recombine/portfolio). Warms every complete election tract,
  // reprices the active one on a term-edit blur, and drops a removed tract.
  const electionPreview = useElectionPreview({
    interestItems,
    buildDraftRequest,
    draftSessionKey,
  });

  // Roll the live modeled economics up to the package Executive Summary (the mock's
  // `reportModelValue`): the carry portfolio's net-to-us into both Target and Max, plus
  // each ELECTED UMI outcome (NPV → Target, undiscounted net → Max). The summary reads
  // this off `interestModelDeltaAtom`, so it tracks the working edits, not the last
  // committed run.
  const setInterestModelDelta = useSetAtom(interestModelDeltaAtom);
  const modelDelta = useMemo(
    () =>
      computeInterestModelDelta(
        preview.portfolio,
        interestItems,
        electionPreview.results
      ),
    [preview.portfolio, interestItems, electionPreview.results]
  );
  useEffect(() => {
    setInterestModelDelta(modelDelta);
  }, [modelDelta, setInterestModelDelta]);
  // Clear the shared delta when this section unmounts — an eval switch remounts it (its
  // `key` carries the page identity), so a prior eval's contribution never lingers on the
  // next one's summary.
  useEffect(
    () => () => setInterestModelDelta(EMPTY_INTEREST_MODEL_DELTA),
    [setInterestModelDelta]
  );

  const usedAssetIds = useMemo(
    () => new Set(interestItems.map((item) => item.tractId)),
    [interestItems]
  );

  // The label is the name stored on the position itself — captured at add time and
  // persisted in the config, so whatever the config carries is what renders, no fetch.
  // A position committed before the name was stored has none, so it shows the tract id.
  const nameFor = useCallback(
    (tractId: string) =>
      interestItems.find((item) => item.tractId === tractId)?.name ?? tractId,
    [interestItems]
  );
  // A Tract the package holds in part says how much of it the package holds, after its name —
  // looked up live, never stored on the position, so a saved config carries no counts. As the
  // package stands, not an evaluation that may be open: the positions price the next run.
  const { labelForTract } = usePartialTracts({ ofPackage: true });
  const displayNameFor = useCallback(
    (tractId: string) =>
      withSubtractCoverage(nameFor(tractId), labelForTract(tractId)),
    [nameFor, labelForTract]
  );

  // Add the picked tracts to the working list (kind derived from instrument type),
  // storing each tract's display name so a reopened eval labels it without a fetch;
  // the last freshly-added tract becomes active.
  const handleAdd = useCallback(
    (assets: PickedAsset[]) => {
      const alreadyModeled = new Set(interestItems.map((item) => item.tractId));
      workingConfig.addInterestItems(
        assets.map((asset) => ({
          tractId: asset.id,
          name: asset.name,
          kind: kindForInstrument(asset.instrumentType),
        }))
      );
      const lastFresh = [...assets]
        .reverse()
        .find((asset) => !alreadyModeled.has(asset.id));
      if (lastFresh) {
        setActiveId(lastFresh.id);
      }
    },
    [interestItems, workingConfig]
  );

  const handleRemove = useCallback(
    (tractId: string) => {
      const remaining = interestItems.filter(
        (item) => item.tractId !== tractId
      );
      const removed = interestItems.find((item) => item.tractId === tractId);
      workingConfig.removeInterestItem(tractId);
      // Drop from the preview session that owns this tract's kind (each is per-kind).
      if (removed?.kind === 'election') {
        electionPreview.drop(tractId);
      } else {
        preview.drop(tractId);
      }
      setActiveId((current) => {
        if (current === tractId || current === null) {
          return remaining[0]?.tractId ?? null;
        }
        return current;
      });
      setOpenItemId((current) => (current === tractId ? null : current));
    },
    [interestItems, workingConfig, preview, electionPreview]
  );

  // Filter the RENDERED nodes by tract name or instrument (OGL/UMI); the underlying
  // list is untouched, so a filtered-out position is never dropped.
  const query = tractQuery.trim().toLowerCase();
  const shownItems = query
    ? interestItems.filter(
        (item) =>
          nameFor(item.tractId).toLowerCase().includes(query) ||
          kindLabel(item.kind).toLowerCase().includes(query)
      )
    : interestItems;

  // Case gate: no case in effect → prompt to choose one (opens Eval Settings,
  // whose Case management accordion is where the case is picked) before any items
  // can be managed.
  if (evaluationCaseId === null) {
    return (
      <OutputSection title="Interest Modeling" defaultExpanded>
        <div className={styles.gate}>
          <span className={styles.gateTitle}>
            Choose an evaluation case first
          </span>
          <span className={styles.gateHint}>
            Interest modeling needs an evaluation case in effect. Pick one in
            Eval Settings, then add assets to start modeling.
          </span>
          <Button
            kind="tertiary"
            size="sm"
            onClick={() => openEvalSettings(true)}
          >
            Choose a case
          </Button>
        </div>
      </OutputSection>
    );
  }

  return (
    <OutputSection title="Interest Modeling" defaultExpanded>
      <div className={styles.explorer}>
        {panelOpen && (
          <aside className={styles.positionsPanel} aria-label="Assets">
            <div className={styles.panelHead}>
              <span className={styles.panelTitle}>Assets</span>
              <Button
                hasIconOnly
                kind="tertiary"
                size="sm"
                renderIcon={Add}
                iconDescription="Add Assets"
                tooltipPosition="left"
                onClick={() => setPickerOpen(true)}
              />
            </div>

            <Search
              id="interest-modeling-tract-search"
              size="sm"
              labelText="Search assets"
              placeholder="Search assets"
              value={tractQuery}
              closeButtonLabelText="Clear asset search"
              onChange={(event) => setTractQuery(event.target.value)}
              onClear={() => setTractQuery('')}
            />

            <div className={styles.treeScroll}>
              {interestItems.length === 0 ? (
                <span className={styles.noMatches}>
                  No positions yet — add an asset to start modeling.
                </span>
              ) : shownItems.length === 0 ? (
                <span className={styles.noMatches}>
                  No assets match “{tractQuery.trim()}”.
                </span>
              ) : (
                // enableTreeviewControllable makes Carbon honor `active`/`selected`
                // (from the TreeView) and the controlled `isExpanded` reactively —
                // without it selection/expansion would be mount-only seeds. Under
                // the flag `onToggle` receives the next expanded BOOLEAN.
                <FeatureFlags enableTreeviewControllable>
                  <TreeView
                    label="Interest positions"
                    hideLabel
                    size="sm"
                    active={activeId ?? undefined}
                    selected={activeId ? [activeId] : []}
                    className={styles.positionsTree}
                  >
                    {shownItems.map((item) => (
                      <TreeNode
                        key={item.tractId}
                        id={item.tractId}
                        label={
                          <span className={styles.nodeLabel}>
                            <span className={styles.nodeName}>
                              {displayNameFor(item.tractId)}
                            </span>
                            <Tag type={kindTagType(item.kind)} size="sm">
                              {kindLabel(item.kind)}
                            </Tag>
                          </span>
                        }
                        onSelect={() => setActiveId(item.tractId)}
                        isExpanded={openItemId === item.tractId}
                        onToggle={(nextExpanded: boolean | undefined) =>
                          setOpenItemId(nextExpanded ? item.tractId : null)
                        }
                      >
                        {/* The expanded body holds the position editor (a later step will
                          build the real one) + the Remove control. Clicks/keys stop
                          here so the tree's own keyboard nav doesn't fire from
                          inside it. */}
                        <li
                          role="none"
                          className={styles.nodeBody}
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <div className={styles.editorBody}>
                            <div className={styles.deleteRow}>
                              <Button
                                kind="danger--tertiary"
                                size="sm"
                                renderIcon={TrashCan}
                                onClick={() => handleRemove(item.tractId)}
                              >
                                Remove Asset
                              </Button>
                            </div>
                            {item.kind === 'carry' ? (
                              // The OGL carry editor (well groups + terms). The
                              // wrapper's onBlur is the reprice trigger: React's
                              // synthetic blur bubbles from any term field, so a
                              // completed edit reprices THIS tract on focus-out
                              // (completeness-gated + de-duped in the hook), with no
                              // change to the editor's internals. The election branch
                              // below mirrors this for UMI items.
                              <div
                                className={styles.carryEditorBlur}
                                onBlur={() => preview.reprice(item, view)}
                              >
                                <CarryEditor
                                  item={item}
                                  wells={wellsByTract.get(item.tractId) ?? []}
                                  wellsResolved={wellsByTract.has(item.tractId)}
                                  wellsLoading={wellsLoading}
                                  workingConfig={workingConfig}
                                />
                              </div>
                            ) : (
                              // The UMI election editor. Its onBlur is the reprice
                              // trigger: a completed term edit reprices THIS tract on
                              // focus-out (completeness-gated + de-duped in the hook).
                              <div
                                className={styles.carryEditorBlur}
                                onBlur={() => electionPreview.reprice(item)}
                              >
                                <ElectionEditor
                                  item={item}
                                  wells={wellsByTract.get(item.tractId) ?? []}
                                  wellsResolved={wellsByTract.has(item.tractId)}
                                  netAcres={netAcresByTract.get(item.tractId)}
                                  workingConfig={workingConfig}
                                />
                              </div>
                            )}
                          </div>
                        </li>
                      </TreeNode>
                    ))}
                  </TreeView>
                </FeatureFlags>
              )}
            </div>
          </aside>
        )}

        <div className={styles.chartArea}>
          {/* Section-local content header (NOT the page CenterHeader, NOT the
            OutputSection accordion heading): prev/next over the full list + the
            panel show/hide toggle. */}
          <div className={styles.contentHeader}>
            {activeItem && (
              <div className={styles.titleNav}>
                <Button
                  hasIconOnly
                  kind="ghost"
                  size="sm"
                  renderIcon={ChevronLeft}
                  iconDescription="Previous asset"
                  tooltipPosition="bottom"
                  disabled={activeIndex <= 0}
                  onClick={() => stepActive(-1)}
                />
                <span className={styles.contentTitle}>
                  {displayNameFor(activeItem.tractId)}
                </span>
                <Tag type={kindTagType(activeItem.kind)} size="sm">
                  {kindLabel(activeItem.kind)}
                </Tag>
                <Button
                  hasIconOnly
                  kind="ghost"
                  size="sm"
                  renderIcon={ChevronRight}
                  iconDescription="Next asset"
                  tooltipPosition="bottom"
                  disabled={
                    activeIndex < 0 || activeIndex >= interestItems.length - 1
                  }
                  onClick={() => stepActive(1)}
                />
              </div>
            )}
            {/* Stays mounted whether or not the panel is open — a control that
              appears only when the panel is hidden is one the user never learns. */}
            <div className={styles.panelToggle}>
              <Button
                hasIconOnly
                kind="tertiary"
                size="sm"
                renderIcon={panelOpen ? SidePanelClose : SidePanelOpen}
                iconDescription={
                  panelOpen ? 'Hide Assets panel' : 'Show Assets panel'
                }
                tooltipPosition="left"
                onClick={() => setPanelOpen((open) => !open)}
              />
            </div>
          </div>

          <div className={styles.contentBody}>
            {!activeItem ? (
              <div className={styles.emptyState}>
                <span className={styles.emptyTitle}>No interest positions</span>
                <span className={styles.emptyHint}>
                  Nothing is currently modeled. Add an asset to build a
                  carried-interest position (OGL) or an unleased-mineral
                  election (UMI).
                </span>
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={Add}
                  onClick={() => setPickerOpen(true)}
                >
                  Add Asset
                </Button>
              </div>
            ) : activeItem.kind === 'carry' ? (
              <CarryPortfolio
                preview={preview}
                view={view}
                onViewChange={setView}
                activeTractId={activeItem.tractId}
                interestItems={interestItems}
                nameFor={nameFor}
                coverageFor={labelForTract}
                wellsByTract={wellsByTract}
              />
            ) : (
              <ElectionResults
                preview={electionPreview}
                item={activeItem}
                onElect={(groupId, election) =>
                  workingConfig.setElectionElected(
                    activeItem.tractId,
                    groupId,
                    election
                  )
                }
              />
            )}
          </div>
        </div>
      </div>

      <AssetPickerTearsheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        usedAssetIds={usedAssetIds}
        onAdd={handleAdd}
      />
    </OutputSection>
  );
}
